// Carga de fuego por sector: RD 164/2025, Anexo I 3.2.1 (dato técnico) y clasificación legal
// del AM 1257 Art. 139 (PE-1). Matriz: filas 1.1, 1.4, 1.5, 1.9 y 1.10.

import {
  CI_RD164, ESCALA_ART139, FORMULA_QS_RD164, NIVELES_RD164, R_RD164,
} from './pe-constantes-normativa';
import { doc, Traza, unirFuentes } from './pe-tipos';
import { mjAKcal, numEc } from './pe-unidades';

const METODO = 'carga_fuego';

// "tabla": el valor tiene que existir en la tabla del RD 164. "manual": cualquier valor con su fuente
// obligatoria y aviso en el PDF (PE-31, Kevin 26/10/2026).
export type OrigenCoeficiente = 'tabla' | 'manual';

export interface MaterialRD164 {
  nombre: string;
  masaKg: number;     // Gi
  qiMJkg: number;     // poder calorífico
  qiOrigen?: OrigenCoeficiente; // 'tabla': Tabla 1.3.3 o 1.3.5; 'manual': fuente obligatoria (PE-14, PE-31)
  qiFuente?: string;
  ci: number;
  ciOrigen?: OrigenCoeficiente;
  ciFuente?: string;  // obligatoria si ciOrigen = 'manual'
}

export interface SectorRD164 {
  nombre: string;
  areaM2: number;     // A
  r: number;
  rOrigen?: OrigenCoeficiente;
  rFuente?: string;   // obligatoria si rOrigen = 'manual'
  materiales: MaterialRD164[];
  separadoCortafuego?: boolean; // false: se evalúa junto con los demás de su grupo (PE-1, PE-28b)
  grupo?: string;
}

export type ClasificacionArt139 = 'leve' | 'ordinario' | 'extra';

export interface ResultadoCargaFuego {
  sector: string;
  sumaMJ: number;            // Σ(qi·Gi·Ci)
  qsMJm2: number;
  qsKcalm2: number;
  nivelRD164: { nivel: number; categoria: 'bajo' | 'medio' | 'alto' };
  clasificacionArt139: ClasificacionArt139;
  trazas: Traza[];
}

const casi = (a: number, b: number) => Math.abs(a - b) < 1e-9;

export function clasificacionArt139(qKcalm2: number): ClasificacionArt139 {
  if (qKcalm2 < ESCALA_ART139.leveMenorQue) return 'leve';
  if (qKcalm2 > ESCALA_ART139.extraMayorQue) return 'extra';
  return 'ordinario';
}

export function nivelRD164(qsMJm2: number) {
  const fila = NIVELES_RD164.find(n => qsMJm2 <= n.hasta) ?? NIVELES_RD164[NIVELES_RD164.length - 1];
  return { nivel: fila.nivel, categoria: fila.categoria, fuente_doc: doc.rd164(fila.linea) };
}

// Valida Ci o R: de la tabla, o "manual" con fuente obligatoria. La traza queda 'por_validar' para que
// el PDF muestre el aviso (PE-31).
function validarCoeficiente(
  tipo: 'Ci' | 'R', valor: number, origen: OrigenCoeficiente, fuente: string | undefined, contexto: string,
): Traza | null {
  if (!(valor > 0)) throw new Error(`${tipo} inválido en ${contexto}: ${valor}`);
  const validos = tipo === 'Ci' ? CI_RD164.normales.concat(CI_RD164.fumigenos) : R_RD164.valores;
  const tabla = tipo === 'Ci' ? CI_RD164 : R_RD164;
  if (origen === 'tabla') {
    if (!validos.some(v => casi(v, valor))) {
      throw new Error(`${tipo} = ${valor} en ${contexto} no está en ${tabla.articulo_o_tabla} del RD 164; use "manual" con su fuente`);
    }
    return null;
  }
  if (!fuente || !fuente.trim()) {
    throw new Error(`${tipo} = ${valor} manual en ${contexto} requiere fuente`);
  }
  return {
    metodo: METODO, norma: tabla.norma, articulo_o_tabla: tabla.articulo_o_tabla,
    umbral: validos.map(v => numEc(v)).join(' / '), valor, unidad: '—', estado: 'por_validar', fuente_doc: tabla.fuente_doc,
    nota: `${tipo} manual en ${contexto}, fuente: ${fuente.trim()}; aviso en el PDF`,
  };
}

export function calcularCargaFuego(sector: SectorRD164): ResultadoCargaFuego {
  if (!(sector.areaM2 > 0)) throw new Error(`Área inválida en ${sector.nombre}`);
  const trazas: Traza[] = [];
  const tr = validarCoeficiente('R', sector.r, sector.rOrigen ?? 'tabla', sector.rFuente, sector.nombre);
  if (tr) trazas.push(tr);

  let sumaMJ = 0;
  for (const m of sector.materiales) {
    if (m.masaKg < 0 || m.qiMJkg < 0) throw new Error(`Masa o poder calorífico negativo en ${m.nombre}`);
    const tc = validarCoeficiente('Ci', m.ci, m.ciOrigen ?? 'tabla', m.ciFuente, `${sector.nombre} / ${m.nombre}`);
    if (tc) trazas.push(tc);
    if (m.qiOrigen === 'manual') {
      if (!m.qiFuente || !m.qiFuente.trim()) throw new Error(`qi manual en ${sector.nombre} / ${m.nombre} requiere fuente`);
      trazas.push({
        metodo: METODO, norma: 'RD 164/2025', articulo_o_tabla: 'Anexo I, Tablas 1.3.3 y 1.3.5', umbral: null,
        valor: m.qiMJkg, unidad: 'MJ/kg', estado: 'por_validar', fuente_doc: doc.rd164('3230-3469'),
        nota: `qi manual en ${sector.nombre} / ${m.nombre}, fuente: ${m.qiFuente.trim()}; aviso en el PDF`,
      });
    }
    sumaMJ += m.qiMJkg * m.masaKg * m.ci;
  }

  const qsMJm2 = (sumaMJ / sector.areaM2) * sector.r;
  const qsKcalm2 = mjAKcal(qsMJm2);
  const nivel = nivelRD164(qsMJm2);
  const clase = clasificacionArt139(qsKcalm2);

  trazas.push({
    metodo: METODO, norma: 'RD 164/2025', articulo_o_tabla: `${FORMULA_QS_RD164.articulo_o_tabla} y Tabla 1.3.1`,
    umbral: nivel.nivel === 8 ? `> ${numEc(13600)}` : `≤ ${numEc(NIVELES_RD164[nivel.nivel - 1].hasta)}`, valor: qsMJm2, unidad: 'MJ/m²',
    estado: 'informativo', fuente_doc: unirFuentes(FORMULA_QS_RD164.fuente_doc, nivel.fuente_doc),
    nota: `Nivel ${nivel.nivel} (${nivel.categoria}); dato técnico, no clasificación legal`,
  });
  trazas.push({
    metodo: METODO, norma: ESCALA_ART139.norma, articulo_o_tabla: ESCALA_ART139.articulo_o_tabla,
    umbral: `leve < ${numEc(ESCALA_ART139.leveMenorQue)}; extra > ${numEc(ESCALA_ART139.extraMayorQue)}`, valor: qsKcalm2,
    unidad: ESCALA_ART139.unidad, estado: 'informativo', fuente_doc: ESCALA_ART139.fuente[clase],
    nota: `Clasificación legal: ${clase}`,
  });

  return { sector: sector.nombre, sumaMJ, qsMJm2, qsKcalm2, nivelRD164: { nivel: nivel.nivel, categoria: nivel.categoria }, clasificacionArt139: clase, trazas };
}

// PE-1 y PE-28b (Kevin 26/10/2026): cada sector tiene "tiene separación cortafuego" (por defecto sí); los
// que no la tienen se evalúan juntos (por "grupo"; sin grupo, todos los no separados forman uno). Área y
// materiales se suman. Con R distintos se toma el mayor:
// el RD 164 no tiene la regla del 10 % de superficie del RD 2267; fija R para todo el sector según
// lo que ocurra en él (Tabla 1.3.4) y como mínimo el Rmin de sus actividades (nota 3 de la Tabla 1.3.5).
// Si un sector aporta la situación a) y otro la b), el grupo iría a 1,8 (Tabla 1.3.4): el responsable
// puede escribir R = 1,8 en el grupo; el motor no lo deduce porque no guarda la causa de cada R.
export interface OpcionesAgrupar {
  agrupar?: boolean; // false: cada sector se evalúa solo aunque no tenga separación cortafuego
}

export function agruparSectores(sectores: SectorRD164[], opciones: OpcionesAgrupar = {}): { sectores: SectorRD164[]; trazas: Traza[] } {
  if (opciones.agrupar === false) return { sectores, trazas: [] };
  const separados = sectores.filter(s => s.separadoCortafuego !== false);
  const grupos: Record<string, SectorRD164[]> = {};
  for (const s of sectores) {
    if (s.separadoCortafuego !== false) continue;
    const clave = s.grupo ?? '(sin separación cortafuego)';
    (grupos[clave] = grupos[clave] ?? []).push(s);
  }
  const trazas: Traza[] = [];
  const unidos: SectorRD164[] = Object.keys(grupos).map(clave => {
    const g = grupos[clave];
    if (g.length === 1) return g[0];
    const rs = g.map(s => s.r);
    const r = Math.max(...rs);
    const manual = g.find(s => s.rOrigen === 'manual' && s.r === r);
    if (rs.some(x => !casi(x, r))) {
      trazas.push({
        metodo: METODO, norma: 'RD 164/2025', articulo_o_tabla: 'Anexo I, Tabla 1.3.4', umbral: null, valor: r,
        unidad: '—', estado: 'informativo', fuente_doc: unirFuentes(doc.rd164('3470-3488'), doc.rd164('3792-3793')),
        nota: `Grupo "${clave}": R distintos (${rs.map(r => numEc(r)).join('; ')}); se usa el mayor (R del sector y Rmin de sus actividades). Si se juntan las situaciones a) y b) de la Tabla 1.3.4, corresponde 1,8`,
      });
    }
    return {
      nombre: g.map(s => s.nombre).join(' + '),
      areaM2: g.reduce((a, s) => a + s.areaM2, 0),
      r,
      rOrigen: manual ? 'manual' : 'tabla',
      rFuente: manual?.rFuente,
      materiales: g.reduce<MaterialRD164[]>((a, s) => a.concat(s.materiales), []),
      separadoCortafuego: false,
      grupo: clave,
    };
  });
  return { sectores: separados.concat(unidos), trazas };
}

// Jerarquización: sectores ordenados de mayor a menor Qs (PASO0 §3.3.1, caso C6).
export function ordenarPorQs<T extends { qsMJm2: number }>(resultados: T[]): T[] {
  return resultados.slice().sort((a, b) => b.qsMJm2 - a.qsMJm2);
}
